import { ReactElement, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import cx from 'clsx';
import { Box } from '@twilio-paste/core/box';

import { EmptyState } from '@twiliointernal/e2-commons-shared-ui';
import {
  oscLayoutCopy,
  EmptyStateVariant,
  routes,
} from '@twiliointernal/e2-commons-shared-definitions';
import { constants, types } from './definitions';
import {
  CompositeSidebar,
  CompositeTopbar,
  Footer,
  UnauthenticatedHeader,
} from './sub';
import PageLoader from '../pageLoader';
import SearchSupport from '../supportSearch';
import { useUserDetails } from '../../hooks';

import styles from './layout.module.css';

const Layout = ({
  searchProps,
  outletOverride,
}: types.LayoutProps): ReactElement => {
  const location = useLocation();
  const {
    doesPageRequireLogin,
    isLoggedIn,
    isLoading: isLoadingAuthState,
  } = useUserDetails();
  const [isSidebarCollapsed, toggleCollapsed] = useState<boolean>(false);
  const shouldUseAuthStyles = isLoggedIn;

  if (isLoadingAuthState) {
    return <PageLoader />;
  }

  const handleToggleCollapsed = (): void => {
    toggleCollapsed(!isSidebarCollapsed);
  };

  const renderAuthenticatedLayout = (): ReactElement => (
    <CompositeSidebar
      isCollapsed={isSidebarCollapsed}
      onToggleCollapse={handleToggleCollapsed}
    >
      <CompositeTopbar />

      {renderMainContent()}
    </CompositeSidebar>
  );

  const renderUnauthenticatedLayout = (): ReactElement => (
    <>
      <UnauthenticatedHeader />

      {doesPageRequireLogin
        ? renderUnauthenticatedEmptyState()
        : renderMainContent()}

      <Footer />
    </>
  );

  const renderUnauthenticatedEmptyState = (): ReactElement => (
    <Box as="main" gridArea="content" className={styles.emptyStateContainer}>
      <EmptyState
        variant={EmptyStateVariant.unauthorized}
        heading={oscLayoutCopy.errors.unauthenticated.heading}
        description={oscLayoutCopy.errors.unauthenticated.description}
      />
    </Box>
  );

  const renderMainContentView = (): ReactElement => {
    /**
     * Temporary fix to hide search on the authenticated ticket submission
     * page. Because both gated/ungated use the same route for react-router
     * there's no way to conditionally pass `searchProps` based on the user's
     * authentication state.
     */
    const isOnAuthenticatedSubmissionPage =
      location.pathname === routes.ticketSubmission;
    const isOnHomePage = location.pathname === routes.home;
    const shouldRenderSearch = searchProps && !isOnAuthenticatedSubmissionPage;
    /**
     * If displaying Wise Owl, should show search as long as we're not on the home page.
     * Passes through with 'true' if not relevant.
     */

    return (
      <Box gridArea="content" className={styles.layoutContentContainer}>
        {shouldRenderSearch && !isOnHomePage && (
          <SearchSupport {...searchProps} />
        )}
        {outletOverride ? outletOverride : <Outlet />}
      </Box>
    );
  };

  const renderMainContent = (): ReactElement => {
    return (
      <>
        <Box
          width="100%"
          gridArea="notification"
          className={styles.notificationContainer}
        >
          {/* OSC Banners go here; content will be static and require release until managed banners are developed */}
        </Box>

        {renderMainContentView()}
      </>
    );
  };

  return (
    <Box
      className={cx(styles.layoutContainer, {
        [styles.layoutContainerAuthenticated]: shouldUseAuthStyles,
        [styles.layoutContainerUnauthenticated]: !shouldUseAuthStyles,
      })}
      display="grid"
      height="100%"
      width="100%"
      id={constants.navigationSkipLinks.mainContent}
    >
      {isLoggedIn && renderAuthenticatedLayout()}
      {!isLoggedIn && renderUnauthenticatedLayout()}
    </Box>
  );
};

export default Layout;
